import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CheckInModal.css';
import { addCheckIn, getVisitsForCheckin } from './Service';
import { getSessionData } from '../../helpers/Util';
import toast from "react-hot-toast";

/* ── Shared canvas helpers ── */
const getDPR = () => window.devicePixelRatio || 1;

const getXY = (e, canvas) => {
  const r = canvas.getBoundingClientRect();
  
  let clientX = 0;
  let clientY = 0;
  
  const touch = (e.changedTouches && e.changedTouches.length > 0) 
    ? e.changedTouches[0] 
    : (e.touches && e.touches.length > 0 ? e.touches[0] : null);
    
  if (touch) {
    clientX = touch.clientX;
    clientY = touch.clientY;
  } else {
    clientX = e.clientX || 0;
    clientY = e.clientY || 0;
  }

  const cssX = clientX - r.left;
  const cssY = clientY - r.top;
  
  return {
    x: cssX * (canvas.width / (r.width || 1)),
    y: cssY * (canvas.height / (r.height || 1))
  };
};


export default function CheckInModal({ isOpen, onClose, patientName = 'Akash Pal', appointmentTime = '8:00 AM', doctorName = 'ANNA WISNIEWSKA', onCheckIn }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);
  const [color, setColor] = useState('#1a1410');
  const [lineWidth, setLineWidth] = useState(2.2);
  const [visitsList, setVisitsList] = useState([]);
  const [selectedVisits, setSelectedVisits] = useState([]);
  const [showApptSelect, setShowApptSelect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryResults, setSummaryResults] = useState([]);
  const [signatureError, setSignatureError] = useState(false);
  const [appointmentError, setAppointmentError] = useState(false);

  useEffect(() => {
    if (selectedVisits.length > 0) {
      setAppointmentError(false);
    }
  }, [selectedVisits]);

  useEffect(() => {
    if (isOpen) {
      handleGetVisitsForCheckin();
      setSignatureError(false);
      setAppointmentError(false);
      setIsEmpty(true);
      setShowSummaryModal(false);
      setShowApptSelect(false);
    }
  }, [isOpen]);

  /* Init canvas on open */
  useEffect(() => {
    if (!isOpen) {
      if (canvasRef.current) delete canvasRef.current.dataset.initialized;
      return;
    }
    const initCanvas = () => {
      const c = canvasRef.current;
      if (!c) return;
      if (c.dataset.initialized === 'true') return;
      const dpr = getDPR();
      let w = c.offsetWidth;
      let h = c.offsetHeight;
      if (!w || !h) {
        const r = c.getBoundingClientRect();
        w = r.width;
        h = r.height;
      }
      if (w === 0 || h === 0) return;
      c.width = w * dpr;
      c.height = h * dpr;
      const ctx = c.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth * dpr;
      c.dataset.initialized = 'true';
    };

    const t1 = setTimeout(initCanvas, 50);
    const t2 = setTimeout(initCanvas, 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isOpen, visitsList, isLoading]); // eslint-disable-line

  const applyStyle = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const dpr = getDPR();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth * dpr;
  };

  const startDraw = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = getXY(e, canvasRef.current); applyStyle(); setSignatureError(false); };
  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const pos = getXY(e, c);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setIsEmpty(false);
  };
  const endDraw = () => { drawing.current = false; };

  const handleClear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
    setSignatureError(false);
  };

  const handleGetVisitsForCheckin = async () => {
    try {
      setIsLoading(true);
      const tokenData = getSessionData('tokenData', {});
      const response = await getVisitsForCheckin(tokenData?.caseId, tokenData?.companyId, '');
      if (response) {
        const visits = response?.data || [];
        setVisitsList(visits);
        if (visits.length === 1) {
          const v = visits[0];
          setSelectedVisits([v.eventId || v.id || v.visitId || v.key || 0]);
        } else {
          setSelectedVisits([]);
        }
      }
    }
    catch (error) {
      console.error('Error fetching visits for check-in:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckIn = async () => {
    let hasError = false;
    if (selectedVisits.length === 0) {
      setAppointmentError(true);
      setShowApptSelect(true);
      hasError = true;
    }
    if (isEmpty) {
      setSignatureError(true);
      hasError = true;
    }
    if (hasError) return;

    try {
      setIsLoading(true);
      const tokenData = getSessionData('tokenData', {});
      const signatureBase64 = !isEmpty && canvasRef.current ? canvasRef.current.toDataURL().split(',')[1] : null;

      const results = [];

      for (const eventId of selectedVisits) {
        const visit = visitsList.find((v, i) => (v.eventId || v.id || v.visitId || v.key || i) === eventId);

        const requestBody = {
          // "userUid": SessionData?.UserUID,
          "patientSignature": signatureBase64,
          "checkInUserId": tokenData?.userId || 'US000000000000000079',
          "caseId": tokenData?.caseId,
          "eventId": eventId,
          "companyId": tokenData?.companyId,
          "doctor": [{
            "doctorId": visit?.doctorId,
            "doctorName": visit?.doctorName,
            "procedureGroupId": visit?.doctorSpecialtyId,
            "procedureGroupName": visit?.doctorSpecialtyName
          }],
          "appointmentDate": new Date(),
        };
        const response = await addCheckIn(requestBody);

        if (response?.responseCode === 200 && response?.data?.result) {
          results.push({ visit, success: true });
        } else {
          results.push({ visit, success: false });
        }
      }

      setSummaryResults(results);
      setShowSummaryModal(true);
      setIsLoading(false);
    }
    catch (error) {
      toast.error('Check-in failed.');
      console.error('Error during check-in:', error);
      setIsLoading(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="cim-overlay">
      <div style={{ position: 'relative' }}>
        <div className="cim-modal">
          {isLoading && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '16px' }}>
              <div style={{ width: 40, height: 40, border: '4px solid #e5ddd3', borderTopColor: '#2a9d8f', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            </div>
          )}

          {/* Header */}
          <div className="cim-header">
            <div className="cim-header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="cim-header-info">
              <h2 className="cim-title">Complete Your Check-In</h2>
              <p className="cim-subtitle">
                <span className="cim-badge">{patientName}</span>
              </p>
            </div>
            <button className="cim-close-btn" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>

          {/* Body */}
          <div className="cim-body">
            {visitsList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7d6e61', fontSize: '15px' }}>
                No visit found to check-in for today.
              </div>
            ) : (
              <>
                <div className="cim-todays-appt-btn" onClick={() => setShowApptSelect(prev => !prev)} style={{ border: appointmentError ? '1px solid #ef4444' : undefined }}>
                  <div className="cim-todays-appt-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line>
                    </svg>
                  </div>
                  <span style={{ fontWeight: 700, color: '#1a1410', flex: 1 }}>Show {visitsList.length > 1 ? 'Appointments' : 'Appointment'}</span>
                  <div className="cim-todays-appt-count">{selectedVisits.length}/{visitsList.length}</div>
                  <div className="cim-todays-appt-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </div>
                </div>

                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: signatureError ? '#ef4444' : '#7d6e61' }}>
                  Please add your signature to complete check-in.
                </p>

                <div className="cim-sig-box" style={{ border: signatureError ? '1px solid #ef4444' : undefined, borderRadius: '12px' }}>
                  <div className="cim-sig-toolbar">
                    <div className="cim-sig-color">
                      Colour:
                      {[['#1a1410', 'c-black'], ['#2a9d8f', 'c-teal'], ['#3b6fd4', 'c-blue']].map(([c, cls]) => (
                        <button key={c} className={`cim-color-btn ${cls} ${color === c ? 'active' : ''}`} onClick={() => setColor(c)} />
                      ))}
                    </div>
                    <div className="cim-sig-size">
                      Size:
                      {[[1.5, 4], [2.2, 6], [3.5, 9]].map(([lw, sz]) => (
                        <button key={lw} className={`cim-size-btn ${lineWidth === lw ? 'active' : ''}`} onClick={() => setLineWidth(lw)}>
                          <div className="cim-size-dot" style={{ width: sz, height: sz }} />
                        </button>
                      ))}
                    </div>
                    <button className="cim-erase-btn" onClick={handleClear}>Clear</button>
                  </div>

                  <div className="cim-canvas-area">
                    {isEmpty && <><div style={{ fontSize: 22 }}>✍️</div><span>Sign here</span></>}
                    <canvas
                      ref={canvasRef}
                      onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                      onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="cim-footer">
            <button className="cim-btn cim-btn-cancel" onClick={onClose}>
              {visitsList.length === 0 ? 'Close' : 'Cancel'}
            </button>
            {visitsList.length > 0 && (
              <button className="cim-btn cim-btn-submit" onClick={handleCheckIn}>
                Check In ({selectedVisits.length})
                {/* <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg> */}
              </button>
            )}
          </div>

        </div>

        {showApptSelect && (
          <>
            <div className="cim-appt-select-backdrop" onClick={() => setShowApptSelect(false)} />
            <div className="cim-appt-select-popover">
              <div className="cim-appt-select-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div className="cim-header-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <path d="M9 16l2 2 4-4"></path>
                  </svg>
                </div>
                <div className="cim-header-info">
                  <span style={{ fontWeight: 700, color: '#2a9d8f', fontSize: '15px', display: 'block' }}>Select {visitsList.length > 1 ? 'Appointments' : 'Appointment'}</span>
                  <p style={{ margin: 0, fontSize: '13px', color: '#7d6e61', marginTop: '2px' }}>{selectedVisits.length} of {visitsList.length} selected for check-in</p>
                </div>
              </div>
              <button className="cim-close-btn" onClick={() => setShowApptSelect(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <p style={{ margin: '8px 20px 0 20px', color: appointmentError ? '#ef4444' : '#7d6e61', fontSize: '13px' }}>
              Please select at least one appointment to check-in.
            </p>
            <div className="cim-appt-select-actions">
              <button className="cim-btn-outline" onClick={() => setSelectedVisits(visitsList.map((v, i) => v.eventId || v.id || v.visitId || v.key || i))}>Select all</button>
              <button className="cim-btn-outline" onClick={() => setSelectedVisits([])}>Unselect all</button>
            </div>
            <div className="cim-appt-list">
              {(() => {
                const groupedVisits = [];
                const groups = {};
                visitsList.forEach((v, i) => {
                  const key = v.doctorId || v.doctorName || doctorName || 'Unknown Doctor';
                  if (!groups[key]) {
                    groups[key] = {
                      key,
                      doctorId: v.doctorId,
                      doctorName: v.doctorName || doctorName || 'Unknown Doctor',
                      visits: []
                    };
                    groupedVisits.push(groups[key]);
                  }
                  groups[key].visits.push({ ...v, originalIndex: i });
                });

                return groupedVisits.map((group) => {
                  const allVisitIdsInGroup = group.visits.map(v => v.eventId || v.id || v.visitId || v.key || v.originalIndex);
                  const isGroupSelected = allVisitIdsInGroup.every(id => selectedVisits.includes(id));
                  const isGroupPartiallySelected = !isGroupSelected && allVisitIdsInGroup.some(id => selectedVisits.includes(id));

                  const toggleGroup = (e) => {
                    e.stopPropagation();
                    if (isGroupSelected) {
                      setSelectedVisits(prev => prev.filter(id => !allVisitIdsInGroup.includes(id)));
                    } else {
                      setSelectedVisits(prev => [...new Set([...prev, ...allVisitIdsInGroup])]);
                    }
                  };

                  return (
                    <div key={group.key} className={`cim-appt-item ${isGroupSelected || isGroupPartiallySelected ? 'selected' : ''}`}>
                      <div className="cim-appt-item-header" onClick={toggleGroup} style={{ cursor: 'pointer' }}>
                        <div className={`cim-checkbox ${isGroupSelected ? 'checked' : ''}`}>
                          {isGroupSelected && <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                          {isGroupPartiallySelected && <svg viewBox="0 0 24 24" fill="none" stroke="#2a9d8f" strokeWidth="3"><line x1="5" y1="12" x2="19" y2="12"></line></svg>}
                        </div>
                        <span style={{ fontWeight: 700, color: '#1a1410', fontSize: '14px', flex: 1 }}>{group.doctorName}</span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#2a9d8f' }}>{group.visits.length}</span>
                      </div>
                      <div className="cim-appt-item-body">
                        {group.visits.map((v, idx) => {
                          const id = v.eventId || v.id || v.visitId || v.key || v.originalIndex;
                          const isSelected = selectedVisits.includes(id);
                          const toggle = (e) => {
                            e.stopPropagation();
                            setSelectedVisits(prev => isSelected ? prev.filter(x => x !== id) : [...prev, id]);
                          };
                          const time = v.eventStartTime ? `${v.eventStartTime} ${v.eventStartTimeType}` : (v.appointmentTime || v.startTime || appointmentTime);
                          const title = v.doctorSpecialtyName || v.procedureGroup || v.specialty || v.department || 'Appointment';
                          const provider = v.providerName || '';

                          return (
                            <div key={id} className="cim-appt-item-row" style={{ marginTop: idx === 0 ? '4px' : '8px', cursor: 'pointer' }} onClick={toggle}>
                              <div className="cim-checkbox-placeholder" />
                              <div className={`cim-small-checkbox ${isSelected ? 'checked' : ''}`}>
                                {isSelected && <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                              </div>
                              <span style={{ color: '#7d6e61', fontSize: '12px', marginLeft: '6px' }}>
                                {time} &nbsp;·&nbsp; {title}{provider ? ` · ${provider}` : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="cim-appt-select-footer" style={{ display: 'flex', gap: '12px' }}>
              <button className="cim-btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowApptSelect(false)}>
                Close
              </button>
              <button className="cim-btn cim-btn-submit" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowApptSelect(false)}>
                Done ({selectedVisits.length})
              </button>
            </div>
          </div>
          </>
        )}
      </div>

      {showSummaryModal && createPortal(
        <div className="cim-overlay">
          <div className="cim-modal" style={{ maxWidth: '450px' }}>
            {/* Header */}
            <div className="cim-header" style={{ borderBottom: '1px solid #e5ddd3', background: '#fdfbf8' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(42,157,143,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#2a9d8f' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div className="cim-header-info">
                <h2 className="cim-title" style={{ color: '#1a1410' }}>Check-In Summary</h2>
                <p className="cim-subtitle">{summaryResults.filter(r => r.success).length} of {summaryResults.length} successful</p>
              </div>
            </div>
            {/* Body */}
            <div className="cim-body" style={{ padding: '16px 20px', maxHeight: '300px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {summaryResults.map((result, idx) => {
                  const v = result.visit || {};
                  const time = v.eventStartTime ? `${v.eventStartTime} ${v.eventStartTimeType}` : (v.appointmentTime || v.startTime || appointmentTime);
                  const title = v.doctorSpecialtyName || v.procedureGroup || v.specialty || v.department || 'Appointment';
                  const provider = v.providerName || '';

                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', borderRadius: '8px', border: '1px solid #e5ddd3', background: '#fff' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a1410' }}>{v.doctorName}</span>
                        <span style={{ fontSize: '12px', color: '#7d6e61' }}>{time} &nbsp;&middot;&nbsp; {title}{provider ? ` \u00B7 ${provider}` : ''}</span>
                      </div>
                      {result.success ? (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#2a9d8f', display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(42,157,143,0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                          Completed
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(239,68,68,0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                          Failed
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Footer */}
            <div className="cim-footer" style={{ justifyContent: 'flex-end' }}>
              <button
                className="cim-btn cim-btn-submit"
                onClick={() => { setShowSummaryModal(false); onCheckIn?.(); onClose?.(); }}
              >
                Okay
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>,
    document.body
  );
}
